import '@xyflow/react/dist/style.css';
import { useCallback, useEffect, useMemo, useState } from "react";
import { CellChange, ReactGrid, TextCell } from "@silevis/reactgrid";
import "@silevis/reactgrid/styles.css";
import { useDispatch, useSelector } from "react-redux";
import { StoreState } from "src/store/configureStore";
import { analysisUpdateRequest } from "src/store/actions/operations";
import { useParams } from 'react-router-dom';
import { Samples } from 'src/mocks/db';
import { StyledButton } from 'src/styled_components/StyledButton';
import { Flex, message, notification, Space, Spin, Upload, UploadProps } from 'antd';
import { FileExcelOutlined, LoadingOutlined } from '@ant-design/icons';
import XLSX from 'xlsx'
import { AsyncStates } from 'src/constants';

const applyChangesToExperiments = (
  changes: CellChange<TextCell>[],
  prevDataset: any
): any => {
  for (const change of changes) {
    if (change.rowId === "CATEGORY") {
      const featureIdx = prevDataset?.features?.indexOf(change.columnId)
      prevDataset.categories[featureIdx].name = change.newCell.text
    } else if (change.rowId === "PARENT_CATEGORY") {
      const featureIdx = prevDataset?.features?.indexOf(change.columnId)
      prevDataset.categories[featureIdx].parentCategory = change.newCell.text
    } else if (change.columnId !== "experiment") {
      const expIdx = prevDataset?.experiments?.indexOf(change.rowId)
      prevDataset.data[expIdx][change.columnId] = change.newCell.text
    }
  }

  return {
    ...prevDataset,
    categories: [...prevDataset.categories],
    data: [...prevDataset.data]
  }
};

export const Categorize = ({ currentStep, previous, next, steps }: any) => {
  const { anid } = useParams<any>()
  const dispatch = useDispatch()

  const combinedDataset = useSelector((state: StoreState) => state.operations.analysisDetailsData.dataset || [])
  const catPlaceholder = useMemo(() => Array.from({ length: combinedDataset?.features?.length }, (_, index) => ({ name: "", id: crypto.randomUUID(), parameter_ids: [combinedDataset?.features?.[index]] })), [combinedDataset?.features])
  const analysis = useSelector((state: StoreState) => state.operations.analysisDetailsData)
  const analysisStatus = useSelector((state: StoreState) => state.operations.analysisDetailsStatus)
  const analysisUpdateStatus = useSelector((state: StoreState) => state.operations.analysisUpdateStatus)

  const [dataset, setDataset] = useState({
    experiments: combinedDataset?.experiments ?? [],
    features: combinedDataset?.features ?? [],
    data: combinedDataset?.data ?? [],
    categories: analysis.categories ?? catPlaceholder
  })
  useEffect(() => {
    setDataset({
      experiments: combinedDataset?.experiments ?? [],
      features: combinedDataset?.features ?? [],
      data: combinedDataset?.data ?? [],
      categories: analysis.categories ?? catPlaceholder
    })
  }, [analysis.categories, catPlaceholder, combinedDataset?.data, combinedDataset?.experiments, combinedDataset?.features])

  const columns = useMemo(() => [{ columnId: "Experiments", width: 150 }, ...dataset.features?.map((feature: string) => ({ columnId: feature, width: 150 }))], [dataset.features]);

  const rows = useMemo(() => [
    { // header row
      rowId: "PARENT_CATEGORY",
      cells: [{ type: "header", text: "Category" }, ...dataset.categories?.map((cat: any, idx: number) => ({ type: "text", text: cat.parentCategory ?? "" }))]
    },
    { // header row
      rowId: "CATEGORY",
      cells: [{ type: "header", text: "Sub-Category" }, ...dataset.categories?.map((cat: any, idx: number) => ({ type: "text", text: cat.name }))]
    },
    { // header row
      rowId: "HEADER",
      cells: [{ type: "header", text: "Experiment" }, ...dataset.features?.map((feature: string) => ({ type: "header", text: Samples.findOne({ id: feature })?.name ?? feature }))]
    },
    ...dataset?.data // body rows
      ?.map((row: any, expIdx: number) => ({
        rowId: dataset?.experiments?.[expIdx],
        cells: [
          {
            type: 'text',
            text: Samples.findOne({ id: dataset?.experiments?.[expIdx] })?.name ?? dataset?.experiments?.[expIdx]
          },
          ...dataset?.features
            ?.map((feature: any) => ({
              type: 'text',
              text: String(row[feature]) ?? ""
            }))]
      })) || []], [dataset.categories, dataset?.data, dataset?.experiments, dataset?.features]);

  const [isTouched, setIsTouched] = useState(false)

  const handleChanges = (changes: CellChange<TextCell>[]) => {
    setIsTouched(true)
    setDataset((prevDataset: any) => applyChangesToExperiments(changes, prevDataset));
  };

  const save = async () => {
    const categories = dataset?.categories
    dispatch(analysisUpdateRequest({ anid, categories }));
    setIsTouched(false)
  }

  const transform = useCallback(async (record: any) => {
    const fileSheets = await record.arrayBuffer()
    const workBook = XLSX.read(fileSheets, { dense: true, sheets: 0 })
    const sheet = workBook.Sheets[workBook.SheetNames[0]]
    const config = {
      parentCategoryColumnIndex: 0,
      categoryColumnIndex: 1,
      featureColumnIndex: 2,
      headerIndex: 0
    }
    const data = sheet.filter((_: any, index: number) => index !== config.headerIndex)
      const mapped: string[] = []
    const categories = dataset.categories
      .map((category: any) => {
        const mapping = data.find((row: any) => category.parameter_ids
            .some((id: string) => {
              const flag = Samples.findOne({ id })?.name === row[config.featureColumnIndex]?.v
              if (flag) mapped.push(row[config.featureColumnIndex].v)
              return flag
            })
        )
        if (!mapping) return category
        return {
          ...category,
          parentCategory: mapping[config.parentCategoryColumnIndex].v,
          name: mapping[config.categoryColumnIndex].v,
        }
      })
      const notMapped = data.filter((row: any) => !mapped.includes(row[config.featureColumnIndex]?.v)).map((row: any) => row[config.featureColumnIndex]?.v)
    if (notMapped.length) notification.error({
      message: (
        <Space direction='vertical' style={{ width: "100%" }} size={'small'}>
          <div>Not found:</div>
          {notMapped.map(parameter => <div key={parameter}>{parameter}</div>)}
        </Space>
      )
    })
    const { isValid, message: errorMessage } = { isValid: true, message: 'error' }

    if (isValid) {
      setDataset(state => ({ ...state, categories }))
      setIsTouched(true)
    } else {
      message.error(errorMessage)
    }

    return false
  }, [dataset.categories])

  const props: UploadProps = useMemo(() => ({
    accept: '.xlsx, .xls, .csv',
    beforeUpload: (record: any) => {
      transform(record)
      return false
    },
    multiple: false,
    maxCount: 1,
  }), [transform])

  return (
    <Spin spinning={analysisStatus === AsyncStates.LOADING} indicator={<LoadingOutlined />}>
      <Space style={{ width: "100%" }} direction='vertical'>
        <Flex>
          <Upload {...props}>
            <StyledButton size='small' type="primary" icon={<FileExcelOutlined />}>Category Mapping</StyledButton>
          </Upload>
        </Flex>
        <div style={{ width: "100%", overflow: "auto" }}>
          <ReactGrid enableRangeSelection stickyLeftColumns={1} rows={rows} columns={columns} enableFillHandle onCellsChanged={handleChanges as any} />
        </div>
        <Flex style={{ marginTop: 24 }} justify="space-between">
          <div>
            {currentStep > 0 && (
              <StyledButton size="small" style={{ margin: '0 8px' }} onClick={previous}>
                Previous
              </StyledButton>
            )}
          </div>
          <Space>
            {isTouched && (
              <StyledButton size="small" onClick={save} loading={analysisUpdateStatus === AsyncStates.LOADING}>
                Save
              </StyledButton>
            )}
            {!isTouched && currentStep < steps.length - 1 && (
              <StyledButton size="small" type="primary" onClick={next} loading={analysisUpdateStatus === AsyncStates.LOADING}>
                Next
              </StyledButton>
            )}
          </Space>
        </Flex>
      </Space>
    </Spin>
  )
}
